<template>
	<div class="list-representation">
		<div class="display-flex flex-row width-100 flex-justify-space-between flex-align-center">
			<div class="display-flex flex-row flex-align-center">
				<SIcon class="mt-1" name="table-rep" />
				<div
					class="ml-3 table-title"
					v-html="markSearchString(getCleanHtml(tableKeyTitle))"
					:class="{ selectable: !isTableInsideTable && !canChooseItems }"
				/>
			</div>
			<div class="menu" v-if="input && input.length > 0">
				Item <span class="item-count">{{ idx + 1 }}</span> of {{ input.length }}
				<AnemicButton :disabled="idx === 0" class="menu-btn" fab @click.native.prevent.stop="prev">
					<SIcon name="left" />
				</AnemicButton>
				<AnemicButton :disabled="idx === input.length - 1" class="menu-btn" fab @click.native.prevent.stop="next">
					<SIcon name="right" />
				</AnemicButton>
			</div>
		</div>
		<div dense class="list-container">
			<div
				v-for="(value, key) in filteredItemFlatten"
				:key="`${idx}-${key}`"
				class="key-value"
				:class="{ 'pointer-events-none': !canChooseItems }"
			>
				<FormulaSelectorMenu
					:formulas="formulas"
					allowKeyboardNavigation
					autoFocus
					v-if="useWithFormulas"
					@show-formula-update="(v) => (showFormula = v)"
					@on-formula-selected="(...args) => handleExtractorClick(key, ...args)"
					:showFormula="showFormula && (isTableItemFocused(key) || clickedItemKey === key)"
				>
					<template #activator>
						<DataTableItem
							:noun="noun"
							:itemKey="key"
							:value="value"
							:nodeUUID="nodeUUID"
							:htmlKey="getKey(key)"
							:isTable="isTable(value)"
							:searchString="searchString"
							@click="selectFocusedItem(key)"
							:canChooseItems="canChooseItems"
							@scroll-to-element="scrollToElement"
							:isFocused="isTableItemFocused(key)"
						/>
					</template>
				</FormulaSelectorMenu>
				<DataTableItem
					v-else
					:itemKey="key"
					:value="value"
					v-on="$listeners"
					:nodeUUID="nodeUUID"
					:htmlKey="getKey(key)"
					@click="itemClick(key)"
					:isTable="isTable(value)"
					:searchString="searchString"
					:canChooseItems="canChooseItems"
					:isFocused="isTableItemFocused(key)"
				/>
			</div>
		</div>
	</div>
</template>

<script>
const RESULTS_KEY = 'results'; // Keep lower case.
import { flatten, isArrayOfObjects } from '@/utils/helpers';
import { markString, escapeHTML } from '../DataInput/DataInputMenu/helpers/helpers';
import FormulaSelectorMenu from './FormulaSelectorMenu';
import { FORMULAS_KEYS } from '../common/RichTextBox/FormulaChip/formulas.const';
import { sanitizeHtml } from '@/utils/helpers';

import isObject from 'lodash/isObject';
import DataTableItem from './DataTableItem';
import { pluralize } from '@/utils/language-service';
import DataInputMenuItemBaseMixin from '@/components/DataInput/DataInputMenu/MenuLists/helpers/DataInputMenuItemBaseMixin';
import { DATA_ITEM_KEY_TO_IGNORE } from '../DataInput/DataInputMenu/helpers/data-input-menu.const';
import * as WorkflowNodeService from '@/utils/workflow-node-service';

export default {
	name: 'DataTableRepresentation',
	mixins: [DataInputMenuItemBaseMixin],
	components: { FormulaSelectorMenu, DataTableItem },
	props: {
		noun: { type: String },
		nodeUUID: { type: String },
		searchString: { type: String },
		isTableFocused: { type: Boolean },
		overrideTableTitle: { type: String },
		isTableInsideTable: { type: Boolean },
		input: { type: Array, required: true },
		listKey: { type: String, required: true },
		didFocusFromBottomToTop: { type: Boolean },
		canChooseItems: { type: Boolean, default: false },
		useWithFormulas: { type: Boolean, default: false },
		tableItemOutputLabels: { type: Object },
	},
	computed: {
		currentFocusedDataItemIndex() {
			return this.itemKeysOnDisplay.findIndex((filteredItem) => filteredItem === this.focusedDataItemKey);
		},
		itemKeysOnDisplay() {
			return Object.keys(this.filteredItemFlatten || {});
		},
		currentItem() {
			return this.input[this.idx];
		},
		currentItemFlatten() {
			if (!this.currentItem) {
				return;
			}
			return flatten(this.currentItem, {}, this.useWithFormulas);
		},
		filteredItemFlatten() {
			if (isObject(this.currentItemFlatten)) {
				const nonTableItems = {};
				const tableItems = {};
				Object.entries(this.currentItemFlatten).forEach(([key, value]) => {
					if (this.isTable(value)) {
						tableItems[key] = value;
					} else {
						if (this.showDataTableItem(value, key)) {
							//  disable this if, if we want to *not* filter the rest of the table results, even if the don't include the searchString
							nonTableItems[key] = value;
						}
					}
				});
				return { ...nonTableItems, ...tableItems };
			}
			return this.currentItemFlatten;
		},
		tableKeyTitle() {
			return this.getItemKeyTitle(this.nodeUUID, this.displayTableKey);
		},
		displayTableKey() {
			if (this.overrideTableTitle) {
				return this.overrideTableTitle;
			}
			let key = this.listKey;
			if (WorkflowNodeService.getValueOfTestResponseOutputLabel(this.tableItemOutputLabels)) {
				key = WorkflowNodeService.getValueOfTestResponseOutputLabel(this.tableItemOutputLabels);
			} else if (String(key).toLowerCase() === RESULTS_KEY && this.noun) {
				key = pluralize(this.noun);
			}
			return this.escapeHTML(key);
		},
	},
	methods: {
		selectFocusedItem(key = this.focusedDataItemKey) {
			setTimeout(() => {
				if (key) {
					this.clickedItemKey = key;
				}
				this.showFormula = true;
			}, 10);
		},
		getCleanHtml(dirtyContent) {
			return sanitizeHtml(dirtyContent);
		},
		isTableItemFocused(key) {
			return this.isTableFocused && key === this.focusedDataItemKey;
		},
		focusOnNextMenuItem() {
			if (this.itemKeysOnDisplay.length === 0) {
				return;
			}
			const currentFocusedIndex = this.currentFocusedDataItemIndex;
			const didGetToBottomOfTheList = this.itemKeysOnDisplay.length <= currentFocusedIndex + 1;
			const upNextItemIndexToFocus = didGetToBottomOfTheList ? 0 : currentFocusedIndex + 1;
			this.focusedDataItemKey = this.itemKeysOnDisplay[upNextItemIndexToFocus];
			if (didGetToBottomOfTheList) {
				this.$emit('table-focus-exit-from-bottom');
			}
		},

		focusOnPreviousMenuItem() {
			if (this.itemKeysOnDisplay.length === 0) {
				return;
			}
			const currentFocusedIndex = this.currentFocusedDataItemIndex;
			const didGetToTopOfTheList = currentFocusedIndex - 1 < 0;
			const upNextItemIndexToFocus = didGetToTopOfTheList ? this.itemKeysOnDisplay.length - 1 : currentFocusedIndex - 1;
			this.focusedDataItemKey = this.itemKeysOnDisplay[upNextItemIndexToFocus];
			if (didGetToTopOfTheList) {
				this.$emit('table-focus-exit-from-top');
			}
		},
		next() {
			if (this.idx < this.input.length - 1) {
				this.idx++;
			}
		},
		prev() {
			if (this.idx > 0) {
				this.idx--;
			}
		},
		isTable(value) {
			return Array.isArray(value) || isArrayOfObjects(value);
		},
		selectFocusedItemWithEnter() {
			this.itemClick(this.focusedDataItemKey);
		},
		itemClick(val) {
			this.$emit('item-clicked', { value: val, index: this.idx, ignoreValue: val === DATA_ITEM_KEY_TO_IGNORE });
		},
		markSearchString(str) {
			return markString(this.searchString, str);
		},
		getKey(key) {
			if (key === DATA_ITEM_KEY_TO_IGNORE) {
				key = 'value';
			}
			key = WorkflowNodeService.getOutputLabelForKey(
				WorkflowNodeService.getNestedValuesOfTestResponseOutputLabel(this.tableItemOutputLabels),
				key
			);
			return this.escapeHTML(key);
		},
		escapeHTML(content) {
			return escapeHTML(content);
		},
		handleExtractorClick(val, formulaKey) {
			if (FORMULAS_KEYS.VALUE_IN_FIRST_ELEMENT.includes(formulaKey)) {
				this.itemClick(val);
			}
		},
		showDataTableItem(value, key) {
			if (!this.searchString || this.searchString.length === 0) {
				return true;
			}
			if (this.tableKeyTitle.toLowerCase().includes(this.searchString.toLowerCase())) {
				return true;
			}
			const htmlKey = this.getKey(key); // filter on the displayed key
			return (
				htmlKey?.toString().toLowerCase().includes(this.searchString.toLowerCase()) ||
				/*
				next line in comment, because it searches in the original key.
				for example: removing next line for key === 'is_admin' and htmlKey = 'Is Admin',
				will cause returning this item as valid in search for "is_admin"
				*/
				// key?.toString().toLowerCase().includes(this.searchString.toLowerCase()) ||
				value?.toString().toLowerCase().includes(this.searchString.toLowerCase())
			);
		},
		scrollToElement(...args) {
			this.$emit('scroll-to-element', ...args);
		},
	},
	data: () => ({
		idx: 0,
		showFormula: false,
		clickedItemKey: null,
		focusedDataItemKey: null,
		formulas: [FORMULAS_KEYS.VALUE_IN_FIRST_ELEMENT],
	}),

	watch: {
		isTableFocused(isFocused) {
			if (isFocused) {
				const firstSelectedItemIndex = this.didFocusFromBottomToTop ? this.itemKeysOnDisplay?.length - 1 || 0 : 0;
				this.focusedDataItemKey = this.itemKeysOnDisplay[firstSelectedItemIndex];
			} else {
				this.focusedDataItemKey = null;
			}
		},
		showFormula(newV) {
			if (!newV) {
				this.clickedItemKey = null;
			}
		},
	},
};
</script>

<style scoped lang="scss">
$grey-color: #444e5f;
$light-grey-color: rgb(53 64 82 / 48%);

.list-representation {
	width: 100%;
	font-size: 14px;
	margin: 3px 0px;
	font-weight: 300;
	line-height: 18px;
	border-radius: 3px;

	.table-title {
		color: $grey-color;
		&.selectable {
			padding: 0px 2px;
			border-radius: 3px;
			background: var(--table-column-background);
		}
	}
	.list-container {
		overflow-y: auto;
		background: inherit;

		.key-value {
			padding-left: 14px;
		}
	}

	.menu {
		text-align: right;
		color: $light-grey-color;
		font-size: small;

		.item-count {
			color: $grey-color;
		}

		.menu-btn {
			background-color: inherit !important;
			border: 1px solid #ccc !important;
			width: 20px;
			height: 20px !important;
			padding: 0 7px !important;
			border-radius: 3px !important;
			top: -1px;
			::v-deep .v-btn__content {
				top: -1px;
			}
		}
	}
}
</style>
